import { z } from "zod";

import type { EmployeeKeyInformationDto } from "@/generated/model/employeeKeyInformationDto";
import { EmploymentStatus } from "@/generated/model/employmentStatus";
import type { UpdateEmployeeKeyInformationRequestDto } from "@/generated/model/updateEmployeeKeyInformationRequestDto";
import { blankToNull } from "@/lib/people";

/** The server's USER upload rule, checked here first so a bad file never uploads. */
export const PHOTO_MIME_TYPES = ["image/jpeg", "image/png", "image/webp"];
export const PHOTO_MAX_BYTES = 5 * 1024 * 1024;

/** Every field as the form holds it: "" means not recorded. */
export interface KeyInformationFormValues {
  firstName: string;
  lastName: string;
  preferredName: string;
  pronouns: string;
  employeeId: string;
  jobTitle: string;
  lineManagerUserId: string;
  roomId: string;
  startDate: string;
  endDate: string;
  employmentStatus: EmploymentStatus | "";
}

/** The part of Key Information that the Employment details box on Add Employee sets. */
export const employmentDetailsShape = {
  jobTitle: z.string().max(100, "Job title must be 100 characters or fewer"),
  lineManagerUserId: z.string(),
  roomId: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  employmentStatus: z.union([z.enum(EmploymentStatus), z.literal("")]),
};

export type EmploymentDetailsFormValues = Pick<
  KeyInformationFormValues,
  keyof typeof employmentDetailsShape
>;

const EMPLOYMENT_DETAIL_FIELDS = Object.keys(
  employmentDetailsShape,
) as (keyof EmploymentDetailsFormValues)[];

/**
 * The server's date rules, shared by every form that sets them: Left needs an
 * end date, and it cannot be before the start date (the same day is fine).
 */
export function checkEmploymentDates<
  T extends Pick<
    KeyInformationFormValues,
    "employmentStatus" | "startDate" | "endDate"
  >,
>(values: T, ctx: z.RefinementCtx<T>): void {
  if (values.employmentStatus === EmploymentStatus.LEFT && !values.endDate) {
    ctx.addIssue({
      code: "custom",
      message: "An end date is required when the status is Left",
      path: ["endDate"],
    });
  }
  if (values.startDate && values.endDate && values.endDate < values.startDate) {
    ctx.addIssue({
      code: "custom",
      message: "The end date cannot be before the start date",
      path: ["endDate"],
    });
  }
}

/** The same rules the server applies, so most mistakes show before saving. */
export const keyInformationSchema = z
  .object({
    firstName: z
      .string()
      .trim()
      .min(1, "First name is required")
      .max(100, "First name must be 100 characters or fewer"),
    lastName: z
      .string()
      .trim()
      .min(1, "Last name is required")
      .max(100, "Last name must be 100 characters or fewer"),
    preferredName: z
      .string()
      .max(100, "Preferred name must be 100 characters or fewer"),
    pronouns: z.string().max(50, "Pronouns must be 50 characters or fewer"),
    employeeId: z
      .string()
      .max(50, "Employee ID must be 50 characters or fewer"),
    ...employmentDetailsShape,
  })
  .superRefine(checkEmploymentDates);

export function keyInformationFormValues(
  keyInformation: EmployeeKeyInformationDto,
): KeyInformationFormValues {
  return {
    firstName: keyInformation.firstName ?? "",
    lastName: keyInformation.lastName ?? "",
    preferredName: keyInformation.preferredName ?? "",
    pronouns: keyInformation.pronouns ?? "",
    employeeId: keyInformation.employeeId ?? "",
    jobTitle: keyInformation.jobTitle ?? "",
    lineManagerUserId: keyInformation.lineManager?.userId ?? "",
    roomId: keyInformation.room?.id ?? "",
    startDate: keyInformation.startDate ?? "",
    endDate: keyInformation.endDate ?? "",
    employmentStatus: keyInformation.employmentStatus ?? "",
  };
}

/** A full PUT: every field is sent, and a blank one clears it. */
export function keyInformationRequest(
  values: KeyInformationFormValues,
): UpdateEmployeeKeyInformationRequestDto {
  return {
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
    preferredName: blankToNull(values.preferredName),
    pronouns: blankToNull(values.pronouns),
    employeeId: blankToNull(values.employeeId),
    jobTitle: blankToNull(values.jobTitle),
    lineManagerUserId: blankToNull(values.lineManagerUserId),
    roomId: blankToNull(values.roomId),
    startDate: blankToNull(values.startDate),
    endDate: blankToNull(values.endDate),
    employmentStatus: values.employmentStatus || null,
  };
}

/** True when anything in the Employment details box was filled in. */
export function hasEmploymentDetails(
  details: EmploymentDetailsFormValues,
): boolean {
  return EMPLOYMENT_DETAIL_FIELDS.some((field) => details[field].trim() !== "");
}

/** Key Information for someone just added: their names and the box, the rest cleared. */
export function employmentDetailsRequest(
  names: Pick<KeyInformationFormValues, "firstName" | "lastName">,
  details: EmploymentDetailsFormValues,
): UpdateEmployeeKeyInformationRequestDto {
  return keyInformationRequest({
    firstName: names.firstName,
    lastName: names.lastName,
    preferredName: "",
    pronouns: "",
    employeeId: "",
    jobTitle: details.jobTitle,
    lineManagerUserId: details.lineManagerUserId,
    roomId: details.roomId,
    startDate: details.startDate,
    endDate: details.endDate,
    employmentStatus: details.employmentStatus,
  });
}

/** Null when the file can be used as a profile photo, otherwise why not. */
export function photoFileProblem(file: File): string | null {
  if (!PHOTO_MIME_TYPES.includes(file.type)) {
    return "Choose a JPEG, PNG or WebP image";
  }
  if (file.size > PHOTO_MAX_BYTES) {
    return "Choose an image of 5 MB or less";
  }
  return null;
}
